import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { onAuthFailed, tokenStore } from "./api";
import safeNoop from "./safeNoop";

const AuthContext = createContext(null);

// Auth state machine:
//   user === null   → still resolving (initial /me probe in flight)
//   user === false  → unauthenticated (no valid session)
//   user === object → authenticated
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    // If we have no Bearer AND no cookie can possibly be set (different
    // origin), the /me call will 401 — that's fine, we settle to "false".
    try {
      const { data: meData } = await api.get("/auth/me");
      setUser(meData);
      return meData;
    } catch (e) {
      // Note: the response interceptor does NOT clear the token here because
      // it explicitly excludes /auth/me. We do it ourselves: if /me fails,
      // any stored token is stale.
      safeNoop("auth.me-probe-failed", e);
      tokenStore.clear();
      setUser(false);
      return null;
    }
  }, []);

  // Initial probe on mount.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Listen for forced logouts dispatched by the axios interceptor (a 401 on
  // any protected route flips us to unauthenticated and lets the router
  // redirect). The handler closes only over module-level imports + the
  // stable setState identity, so the effect runs exactly once on mount.
  useEffect(() => {
    const unsubscribe = onAuthFailed(() => {
      tokenStore.clear();
      setUser(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    const { data: loginData } = await api.post("/auth/login", { email, password });
    if (loginData?.access_token) tokenStore.set(loginData.access_token);
    setUser(loginData.user);
    return loginData.user;
  };

  // Passwordless sign-in to the seeded demo account. The backend looks up
  // the demo user by env config (DEMO_USER_EMAIL) and mints a session
  // directly — no credential ever ships in the frontend bundle.
  const demoLogin = async () => {
    const { data: loginData } = await api.post("/auth/demo-login");
    if (loginData?.access_token) tokenStore.set(loginData.access_token);
    setUser(loginData.user);
    return loginData.user;
  };

  const register = async (email, password, name) => {
    const { data: regData } = await api.post("/auth/register", { email, password, name });
    if (regData?.access_token) tokenStore.set(regData.access_token);
    setUser(regData.user);
    return regData.user;
  };

  const logout = async () => {
    // Best-effort server logout: even if it fails (network, 401) we still
    // drop local state so the UI can transition cleanly. The server-side
    // failure has no recovery path beyond what we do next anyway.
    try {
      await api.post("/auth/logout");
    } catch (e) {
      safeNoop("auth.logout-failed", e);
    }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, demoLogin, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
